import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import throttle from 'lodash/throttle'
import useApi from '../../hooks/useApi'
import useTranslation from '../../hooks/useTranslation'
import PdfView from '../pdf/PdfView'
import PdfErrorBoundary from '../pdf/PdfErrorBoundary'
import { useStore } from '../pageView/Page'

const Wrapper = styled.div`
  width: 100%;
`

const PdfWrapper = styled.div`
  width: 100%;
  height: ${({ $height }) => `${$height}px`};
`

const ImageWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`

const PreviewImage = styled.img`
  max-width: 100%;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
`

const LinkCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem;
  border: 1px solid ${({ theme }) => theme.border?.default || '#e5e7eb'};
  border-radius: 8px;
  background: ${({ theme }) => theme.page?.containerBg || '#fff'};
`

const LinkUrl = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.font?.subtle || '#6b7280'};
  word-break: break-all;
`

const OpenButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  background: ${({ theme }) => theme.color?.primary || '#2563eb'};
  color: #fff;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  align-self: flex-start;
  &:hover { opacity: 0.88; }
`

const Placeholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${({ theme }) => theme.font?.subtle || '#9ca3af'};
  font-size: 0.875rem;
  border: 2px dashed ${({ theme }) => theme.border?.default || '#e5e7eb'};
  border-radius: 8px;
`

const ErrorMsg = styled.div`
  color: ${({ theme }) => theme.color?.danger || '#dc2626'};
  font-size: 0.875rem;
  padding: 1rem;
`

// `file` previews one given file ({ fileType, fileUrl, fileName }); without it the
// preview reads the record in the form, which is what a document page shows.
const DocumentPreview = ({ file } = {}) => {
  const [formState] = useStore(s => s)
  const state = file ?? formState
  const { getMeStream } = useApi()
  const { t } = useTranslation()

  const wrapperRef = useRef(null)
  const isMountedRef = useRef(true)

  const [width, setWidth] = useState(400)
  const [blob, setBlob] = useState(null)
  const [objectUrl, setObjectUrl] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fileType = state?.fileType
  const fileUrl  = state?.fileUrl
  const linkUrl  = state?.linkUrl
  const fileName = state?.fileName

  // Track mount state
  useEffect(() => {
    isMountedRef.current = true
    return () => { isMountedRef.current = false }
  }, [])

  // Measure container width and keep it in sync with panel resize
  useEffect(() => {
    const updateWidth = () => {
      if (!isMountedRef.current || !wrapperRef.current) return
      setWidth(wrapperRef.current.getBoundingClientRect().width)
    }

    const throttledResize = throttle(updateWidth, 1000)
    const throttledObserver = throttle(updateWidth, 1000)

    updateWidth()
    window.addEventListener('resize', throttledResize)

    const obs = new ResizeObserver(throttledObserver)
    if (wrapperRef.current) obs.observe(wrapperRef.current)

    return () => {
      window.removeEventListener('resize', throttledResize)
      obs.disconnect()
    }
  }, [])

  // Fetch file blob when fileUrl changes
  useEffect(() => {
    if (!fileUrl || fileType === 'link' || fileType === 'richtext') return

    let cancelled = false
    setLoading(true)
    setError(null)
    setBlob(null)
    if (objectUrl) URL.revokeObjectURL(objectUrl)
    setObjectUrl(null)

    getMeStream(`file/${fileUrl}?render=document`).then(file => {
      if (cancelled) return
      if (!file || file.error) {
        setError(t('documents.document.preview.error'))
        setLoading(false)
        return
      }
      const url = URL.createObjectURL(file)
      setBlob(file)
      setObjectUrl(url)
      setLoading(false)
    }).catch(() => {
      if (!cancelled) {
        setError(t('documents.document.preview.error'))
        setLoading(false)
      }
    })

    return () => { cancelled = true }
  }, [fileUrl, fileType])

  // Revoke object URL on unmount
  useEffect(() => {
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [objectUrl])

  const renderContent = () => {
    if (!fileType) return <Placeholder>{t('documents.document.preview.noType')}</Placeholder>
    if (fileType === 'richtext') return null

    if (fileType === 'link') {
      if (!linkUrl) return <Placeholder>{t('documents.document.preview.noLink')}</Placeholder>
      return (
        <LinkCard>
          <LinkUrl>{linkUrl}</LinkUrl>
          <OpenButton href={linkUrl} target="_blank" rel="noreferrer noopener">
            {t('documents.document.preview.openLink')}
          </OpenButton>
        </LinkCard>
      )
    }

    if (loading) return <Placeholder>{t('documents.document.preview.loading')}</Placeholder>
    if (error)   return <ErrorMsg>{error}</ErrorMsg>
    if (!blob)   return <Placeholder>{t('documents.document.preview.noFile')}</Placeholder>

    if (fileType === 'pdf') {
      return (
        <PdfWrapper $height={width * 1.41 + 65}>
          <PdfErrorBoundary>
            <PdfView pdf={blob} width={width} />
          </PdfErrorBoundary>
        </PdfWrapper>
      )
    }

    if (fileType === 'image') {
      return (
        <ImageWrapper>
          <PreviewImage src={objectUrl} alt={fileName || t('documents.document.preview.image.alt')} />
        </ImageWrapper>
      )
    }

    return <Placeholder>{t('documents.document.preview.unsupported')}</Placeholder>
  }

  return (
    <Wrapper ref={wrapperRef}>
      {renderContent()}
    </Wrapper>
  )
}

export default DocumentPreview
